import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { ClientCommunication } from '../types'
import { fmtDate } from '../lib/format'

const COMM_TYPES = [
  'welcome',
  'selection_reminder',
  'milestone_congrats',
  'co_documentation',
  'warranty_ack',
  'post_move_30d',
  'follow_up_6mo',
  'warranty_walk_11mo',
] as const

export default function CommsTab() {
  const [drafts, setDrafts] = useState<ClientCommunication[]>([])
  const [sent, setSent] = useState<ClientCommunication[]>([])
  const [error, setError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const [opMessage, setOpMessage] = useState<string | null>(null)
  const [editing, setEditing] = useState<ClientCommunication | null>(null)
  const [body, setBody] = useState('')
  const [subject, setSubject] = useState('')

  useEffect(() => {
    refresh()
  }, [])

  async function refresh() {
    try {
      setDrafts(await api.clientComms.list({ status: 'drafted' }))
      setSent((await api.clientComms.list({ status: 'sent' })).slice(0, 25))
    } catch (e) {
      setError((e as Error).message)
    }
  }

  async function scanNow() {
    setScanning(true)
    setOpMessage(null)
    try {
      const r = await api.clientComms.scan()
      setOpMessage(
        `Scan: ${r.detected} detected · ${r.drafted} drafted · ${r.auto_sent} auto-sent · ${r.failed} failed`
      )
      await refresh()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setScanning(false)
    }
  }

  function openEdit(c: ClientCommunication) {
    setEditing(c)
    setSubject(c.subject)
    setBody(c.body)
  }
  async function saveEdits() {
    if (!editing) return
    await api.clientComms.update(editing.id, { subject, body })
    setEditing(null)
    await refresh()
  }
  async function sendNow(id: string) {
    if (!confirm('Send this email now?')) return
    try {
      await api.clientComms.send(id)
      await refresh()
    } catch (e) {
      setError((e as Error).message)
    }
  }
  async function deleteDraft(id: string) {
    if (!confirm('Delete this draft?')) return
    await api.clientComms.delete(id)
    await refresh()
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Client Comms</h2>
          <p className="muted small">
            Auto-drafted client emails by lifecycle trigger. Review, edit, send.
          </p>
        </div>
        <button className="btn-primary" onClick={scanNow} disabled={scanning}>
          {scanning ? 'Scanning…' : '↻ Scan triggers'}
        </button>
      </div>

      {error && <div className="card alert mb-2">{error}</div>}
      {opMessage && <div className="card mb-2"><strong>OK.</strong> {opMessage}</div>}

      <h3>Drafts ({drafts.length})</h3>
      {drafts.length === 0 ? (
        <p className="muted">No drafts. Daily scan runs automatically — or click Scan triggers.</p>
      ) : (
        drafts.map((c) => (
          <div key={c.id} className="card mb-2">
            <div className="row-between">
              <div>
                <div className="row-title">
                  <span
                    style={{
                      background: '#7fa3c3',
                      color: '#fff',
                      fontSize: '0.7rem',
                      padding: '1px 6px',
                      borderRadius: 3,
                      marginRight: 6,
                      textTransform: 'uppercase',
                    }}
                  >
                    {c.comm_type}
                  </span>
                  {c.subject}
                </div>
                <div className="muted small">
                  → {c.client_email} · {c.project_name} · drafted {fmtDate(c.created_at)}
                </div>
                <details className="mt-1">
                  <summary className="muted small" style={{ cursor: 'pointer' }}>
                    Preview body
                  </summary>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.85rem',
                      marginTop: 4,
                      background: 'rgba(255,255,255,0.04)',
                      padding: 8,
                      borderRadius: 4,
                    }}
                  >
                    {c.body}
                  </pre>
                </details>
              </div>
              <div className="row gap-2">
                <button className="btn-primary small" onClick={() => sendNow(c.id)}>
                  Send
                </button>
                <button className="btn-ghost small" onClick={() => openEdit(c)}>
                  Edit
                </button>
                <button className="btn-ghost small" onClick={() => deleteDraft(c.id)}>
                  ✕
                </button>
              </div>
            </div>
          </div>
        ))
      )}

      {editing && (
        <div className="modal-bg" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit draft</h3>
            <label className="form-row">
              <span>Subject</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </label>
            <label className="form-row">
              <span>Body</span>
              <textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} />
            </label>
            <div className="row gap-2 mt-2">
              <button className="btn-primary" onClick={saveEdits}>
                Save
              </button>
              <button className="btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <h3 className="mt-3">Recently sent ({sent.length})</h3>
      {sent.length === 0 ? (
        <p className="muted small">Nothing sent yet.</p>
      ) : (
        sent.map((c) => (
          <div key={c.id} className="card mb-2" style={{ background: 'rgba(46,160,67,0.06)' }}>
            <div className="row-between">
              <div>
                <div className="row-title">{c.subject}</div>
                <div className="muted small">
                  → {c.client_email} · {c.project_name} · sent {fmtDate(c.sent_at)}
                </div>
              </div>
              <span className="muted small">{c.comm_type}</span>
            </div>
          </div>
        ))
      )}

      <details className="mt-3">
        <summary className="muted small" style={{ cursor: 'pointer' }}>
          Available trigger types
        </summary>
        <ul>
          {COMM_TYPES.map((t) => (
            <li key={t} className="small">
              {t}
            </li>
          ))}
        </ul>
      </details>
    </section>
  )
}
